import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Wifi } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1745301558339-44eb3217d5da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBpbmRpYW4lMjBsdXh1cnklMjBob21lJTIwbGl2aW5nJTIwcm9vbSUyMGludGVyaW9yfGVufDB8fHx8MTc4NTIxNjQ5N3ww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
    return (
        <section className="relative overflow-hidden" data-testid="hero-section">
            <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
            <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-radial-cyan pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 lg:pt-24 lg:pb-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1.5 pr-4 py-1.5 text-xs font-medium text-[#1B2E7A] shadow-sm"
                        >
                            <span className="bg-[#1BB6F0] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">New</span>
                            Starts at just ₹17,999 · 1-Hour Free Installation
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.05 }}
                            className="mt-6 text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[1.02] text-[#0B1440]"
                            data-testid="hero-title"
                        >
                            Make your home smarter.<br />
                            <span className="text-brand-gradient">Without replacing it.</span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.15 }}
                            className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl"
                        >
                            GoSmartNest turns the appliances you already own into a connected,
                            intelligent home — simple, affordable and reliably installed in an hour.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.25 }}
                            className="mt-8 flex flex-wrap items-center gap-3"
                        >
                            <Link
                                to="/contact"
                                data-testid="hero-cta-consultation"
                                className="inline-flex items-center gap-1.5 bg-[#1B2E7A] hover:bg-[#13215A] text-white rounded-full px-7 py-3.5 font-medium shadow-md hover:shadow-lg transition-[background-color,transform,box-shadow] hover:-translate-y-0.5"
                            >
                                Get Free Consultation <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/experience-smart-living"
                                data-testid="hero-cta-experience"
                                className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-[#1BB6F0] text-[#1B2E7A] rounded-full px-7 py-3.5 font-medium transition-colors"
                            >
                                <Sparkles className="w-4 h-4 text-[#1BB6F0]" /> Experience Smart Living →
                            </Link>
                        </motion.div>

                        <div className="mt-10 flex items-center gap-6 text-xs text-slate-500">
                            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#1BB6F0] animate-soft-pulse" /> Live in 4 cities</div>
                            <div className="flex items-center gap-2"><Wifi className="w-3.5 h-3.5" /> Works with Alexa · Google Home · Siri</div>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="relative rounded-[28px] overflow-hidden border border-slate-200 shadow-xl shadow-[#1B2E7A]/10 bg-white">
                            <img src={HERO_IMG} alt="Modern smart living room" className="w-full h-[440px] lg:h-[560px] object-cover" />
                            {/* Floating dashboard cards */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                                className="absolute top-5 left-5 backdrop-blur-xl bg-white/85 border border-white/60 rounded-2xl px-4 py-3 shadow-lg"
                            >
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Living Room</div>
                                <div className="text-sm font-semibold text-[#1B2E7A] flex items-center gap-2 mt-0.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#1BB6F0] animate-soft-pulse" /> Lights ON · 4/6 active
                                </div>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.7 }}
                                className="absolute bottom-5 right-5 backdrop-blur-xl bg-[#0B1440]/85 text-white border border-white/10 rounded-2xl px-4 py-3 shadow-lg"
                            >
                                <div className="text-[10px] uppercase tracking-widest text-[#1BB6F0] font-semibold">Climate</div>
                                <div className="text-sm font-semibold mt-0.5">AC set to 22°C</div>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.9 }}
                                className="absolute bottom-24 left-5 backdrop-blur-xl bg-white/85 border border-white/60 rounded-2xl px-4 py-3 shadow-lg"
                            >
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Scene</div>
                                <div className="text-sm font-semibold text-[#1B2E7A] mt-0.5">Movie Night activated</div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
